import React, { useContext } from 'react';

import { useAtom } from 'jotai';
import { Header, Message } from 'semantic-ui-react';

import { constraintsAtom } from './atoms';
import { HEADER_DESCRIPTION, KPI_METRIC_TEXT } from './constants';
import ForecastTable from './ForecastTable';
import { useBaselineData, useOrganicBaselineRevenue } from './hooks/useBaselineData';
import { useOptimizationCalculation } from './hooks/useCalculations';
import { useFilteredData, useFeatureStats } from './hooks/useData';
import Form from './ScenarioForm';
import { kpiAtom } from '../../atoms';
import { FeatureAliasesContext } from '../../context/MMMFeatureAliases';
import { FILTER_FEATURES_BY } from '../MarketingMixModeling/constants';
import { useResponseCurves, useTrainingAndTestDates } from '../MarketingMixModeling/hooks/useModelData';

const ScenarioPlanner = ({ rawData, trainingInfo, responseCurves: responseCurvesRaw, seasonality }) => {
  const [kpi] = useAtom(kpiAtom);
  const [constraints] = useAtom(constraintsAtom);
  const isConversionKPI = kpi === KPI_METRIC_TEXT.conversions;
  const { trainingData: trainingDataRaw, trainingStats: trainingStatsRaw } = trainingInfo;

  const { featureAliasesMap: featureNameMap } = useContext(FeatureAliasesContext);
  const { responseCurves, trainingData, trainingStats } = useFilteredData(responseCurvesRaw, trainingDataRaw, trainingStatsRaw, FILTER_FEATURES_BY);
  const { trainingDateRange, trainingDataDateRange } = useTrainingAndTestDates(rawData);

  const responseCurveFeatures = Object.keys(responseCurves);
  const { featureStats, loadingCube } = useFeatureStats({ trainingData, trainingDateRange: trainingDataDateRange, responseCurveFeatures });
  const { responseCurvesArr } = useResponseCurves({ responseCurves });
  const { baseline } = useBaselineData({
    responseCurves,
    trainingStats,
    featureStats,
    constraints,
  });
  const { organicBaselineRevenue, organicBaselineRevenuePerDay } = useOrganicBaselineRevenue({ seasonality });

  const {
    isDirty,
    fieldValues,
    updateFieldValues,
    submitForecast,
    optimization,
    optimizationError,
  } = useOptimizationCalculation({
    baseline,
    organicBaselineRevenue: organicBaselineRevenuePerDay,
    responseCurves: responseCurvesArr,
    constraints,
    isConversionKPI,
  });

  return (
    <>
      <Header as="h4" content={HEADER_DESCRIPTION} style={{ marginBottom: 2, fontWeight: 400 }} />
      <Form
        {...{
          isConversionKPI,
          trainingDateRange,
          baseline,
          featureNameMap,
          featureStats,
          submitForecast,
          loadingCube,
        }}
      />
      {!isDirty && optimization && (
        optimizationError ? (
          <Message negative>
            <Message.Header>Optimization Error</Message.Header>
            <p>{optimizationError}</p>
          </Message>
        ) : (
          <ForecastTable
            {...{
              isConversionKPI,
              isDirty,
              optimization,
              constraints,
              featureNameMap,
              featureStats,
              updateFieldValues,
              fieldValues,
              responseCurves: responseCurvesArr,
              baseline: organicBaselineRevenue,
            }}
          />
        )
      )}
    </>
  );
};

export default ScenarioPlanner;
